import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Trash2, ShieldAlert, X, Loader2, Ban, CheckCircle2, Clock, XCircle } from "lucide-react";
import { api, formatApiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// --------------------------------------------------------------------
// Delete Account — request-based per policy at /account-deletion.
// Any role can request; owner requests cascade to the whole workshop.
// --------------------------------------------------------------------
export default function DeleteAccount() {
  const { user } = useAuth();
  const isOwner = user?.role === "owner";

  const [current, setCurrent] = useState(undefined);   // undefined = loading, null = none
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ reason: "", password: "", confirm: false });

  const load = async () => {
    try {
      const r = await api.get("/account-deletion/request");
      setCurrent(r.data || null);
    } catch (e) {
      toast.error(formatApiError(e));
      setCurrent(null);
    }
  };
  useEffect(() => { load(); }, []);

  const isPending = current?.status === "pending";

  const submit = async () => {
    if (!form.password) return toast.error("Please enter your password.");
    if (!form.confirm) return toast.error("Please acknowledge the impact of this action.");
    setBusy(true);
    try {
      const r = await api.post("/account-deletion/request", {
        password: form.password,
        reason: form.reason,
        confirm_understanding: form.confirm,
      });
      setCurrent(r.data);
      setForm({ reason: "", password: "", confirm: false });
      toast.success(`Request submitted (${r.data.ticket_reference}). Check your email.`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!current?.id) return;
    if (!window.confirm("Cancel this deletion request? Your account will stay active.")) return;
    setBusy(true);
    try {
      await api.delete(`/account-deletion/request/${current.id}`);
      toast.success("Deletion request cancelled");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (current === undefined) {
    return (
      <div className="bg-white rounded-xl border border-[#E4E7EC] p-10 flex items-center justify-center text-[#667085]" data-testid="delete-account-loading">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="delete-account-root">
      <Header />

      {isPending ? (
        <PendingCard req={current} onCancel={cancel} busy={busy} isOwner={isOwner} />
      ) : (
        <>
          {current && current.status !== "pending" && <HistoryCard req={current} />}
          <RequestForm
            form={form} setForm={setForm}
            onSubmit={submit} busy={busy}
            isOwner={isOwner}
            userEmail={user?.email}
          />
        </>
      )}
    </div>
  );
}

// -------------------- Sub-components --------------------

function Header() {
  return (
    <header>
      <h2 className="text-[16px] md:text-[18px] font-bold text-[#1d3557]">Delete Account</h2>
      <p className="text-[12.5px] text-[#667085] mt-0.5">
        Ask the SparesHub team to permanently delete your Fender account and associated personal
        data. Read the full policy at{" "}
        <a href="/account-deletion" target="_blank" rel="noreferrer" className="text-[#457b9d] hover:text-[#e63946] font-semibold underline">
          /account-deletion
        </a>.
      </p>
    </header>
  );
}

function OwnerWarning({ userEmail }) {
  return (
    <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/8 p-4 flex items-start gap-3" data-testid="delete-account-owner-warning">
      <ShieldAlert size={20} className="text-[#e63946] shrink-0 mt-0.5" />
      <div className="text-[13px] text-[#1d3557] leading-relaxed">
        <div className="font-bold text-[#e63946] mb-1">You are the workshop owner.</div>
        Approving this request will <b>close the entire workshop</b>, revoke access for every
        team member, and eventually remove all associated job cards, invoices, and operational
        records (subject to legal retention).
        {userEmail && (
          <>
            {" "}The SparesHub team will contact you at{" "}
            <span className="notranslate font-semibold text-[#1d3557]">{userEmail}</span>
            {" "}before proceeding.
          </>
        )}
      </div>
    </div>
  );
}

function RequestForm({ form, setForm, onSubmit, busy, isOwner, userEmail }) {
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] overflow-hidden" data-testid="delete-account-form">
      <div className="p-4 border-b border-[#F1FAEE]">
        <h3 className="text-[13.5px] font-bold text-[#1d3557]">Request Deletion</h3>
        <p className="text-[12px] text-[#667085] mt-0.5">
          We&apos;ll email the SparesHub team automatically. You&apos;ll receive a confirmation with a
          ticket reference. Processing time is per applicable law.
        </p>
      </div>

      <div className="p-4 space-y-4">
        {isOwner && <OwnerWarning userEmail={userEmail} />}

        <label className="block">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085] mb-1">
            Reason (optional but helpful)
          </div>
          <textarea
            rows={3}
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            placeholder="e.g. Closing my workshop, switching software, testing…"
            data-testid="delete-account-reason"
            className="w-full rounded-md border border-[#E4E7EC] p-2.5 text-[13px] focus:border-[#457b9d] outline-none resize-none"
            maxLength={1000}
          />
          <div className="text-[10.5px] text-[#a8dadc] mt-1 text-right">
            {form.reason.length}/1000
          </div>
        </label>

        <label className="block">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-[#667085] mb-1">
            Confirm your password
          </div>
          <input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="Current login password"
            data-testid="delete-account-password"
            autoComplete="current-password"
            className="w-full rounded-md border border-[#E4E7EC] px-3 py-2 text-[13px] focus:border-[#457b9d] outline-none"
          />
          <div className="text-[11px] text-[#667085] mt-1">
            We ask again to make sure it&apos;s really you — same password you use to sign in.
          </div>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.checked })}
            data-testid="delete-account-confirm"
            className="w-4 h-4 accent-[#e63946] mt-0.5"
          />
          <span className="text-[13px] text-[#1d3557] leading-relaxed">
            {isOwner
              ? "I understand this will request the closure of my Fender account AND my workshop, including all team members' access and operational records (subject to legal retention)."
              : "I understand this will request permanent deletion of my Fender account and associated personal data."}
          </span>
        </label>
      </div>

      <div className="px-4 py-3 bg-[#F7F9FC] border-t border-[#F1FAEE] flex items-center gap-2 justify-end">
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy || !form.password || !form.confirm}
          data-testid="delete-account-submit"
          className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          {busy ? <><Loader2 size={13} className="animate-spin" /> Submitting…</> : <><Trash2 size={13} /> Submit deletion request</>}
        </button>
      </div>
    </div>
  );
}

function PendingCard({ req, onCancel, busy, isOwner }) {
  return (
    <div className="bg-white rounded-xl border border-[#F79009]/40 overflow-hidden" data-testid="delete-account-pending">
      <div className="p-4 border-b border-[#F1FAEE] bg-gradient-to-r from-[#FEF0C7] to-white">
        <div className="flex items-start gap-3">
          <Clock size={20} className="text-[#B54708] shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-[14px] font-bold text-[#1d3557]">
              Deletion request pending review
            </h3>
            <p className="text-[12.5px] text-[#667085] mt-1">
              Ticket <span className="font-mono font-semibold text-[#1d3557]">{req.ticket_reference}</span>
              {" · "}submitted {req.requested_at ? fmtDateTime(req.requested_at) : "just now"}.
              The SparesHub team is reviewing your request; you&apos;ll hear from them shortly.
              {isOwner && " Because you are the owner, approval will close the entire workshop."}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-3 text-[13px] text-[#1d3557]">
        {req.reason && (
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mb-1">
              Reason you provided
            </div>
            <div className="p-3 rounded-md bg-[#F7F9FC] border border-[#E4E7EC] whitespace-pre-wrap">{req.reason}</div>
          </div>
        )}
        <div className="text-[12px] text-[#667085]">
          Need to change your mind? You can cancel this request while it&apos;s still pending —
          once SparesHub starts processing it, only they can stop it.
        </div>
      </div>

      <div className="px-4 py-3 bg-[#F7F9FC] border-t border-[#F1FAEE] flex items-center justify-end">
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          data-testid="delete-account-cancel"
          className="h-10 px-4 rounded-md border border-[#E4E7EC] bg-white text-[12.5px] font-semibold text-[#1d3557] hover:border-[#e63946]/40 hover:text-[#e63946] disabled:opacity-40 inline-flex items-center gap-1.5"
        >
          <X size={13} /> Cancel request
        </button>
      </div>
    </div>
  );
}

function HistoryCard({ req }) {
  const chip = useMemo(() => {
    const map = {
      approved:  { bg: "bg-emerald-500/12 text-emerald-700 border-emerald-500/30", Icon: CheckCircle2, label: "Approved" },
      rejected:  { bg: "bg-red-500/12 text-red-700 border-red-500/30",             Icon: XCircle,     label: "Rejected" },
      cancelled: { bg: "bg-zinc-500/12 text-zinc-700 border-zinc-500/30",          Icon: Ban,         label: "Cancelled" },
      completed: { bg: "bg-[#1d3557]/12 text-[#1d3557] border-[#1d3557]/30",       Icon: CheckCircle2, label: "Completed" },
    };
    return map[req.status] || { bg: "bg-zinc-500/12 text-zinc-700 border-zinc-500/30", Icon: AlertTriangle, label: req.status };
  }, [req.status]);
  const { Icon, bg, label } = chip;
  return (
    <div className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid={`delete-account-history-${req.status}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-md grid place-items-center border ${bg}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13px] font-bold text-[#1d3557]">Last request: {label}</div>
            <span className="font-mono text-[11.5px] text-[#667085]">{req.ticket_reference}</span>
          </div>
          <div className="text-[12px] text-[#667085] mt-0.5">
            {req.reviewed_at ? `Actioned ${fmtDateTime(req.reviewed_at)}` : `Submitted ${fmtDateTime(req.requested_at)}`}
          </div>
          {req.admin_notes && (
            <div className="mt-2 text-[12.5px] text-[#1d3557] p-2.5 rounded-md bg-[#F7F9FC] border border-[#E4E7EC]">
              <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] mr-1">SparesHub note:</span>
              {req.admin_notes}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
